

// Reading api2.govglance.org's archive of city and county government websites.
//
// Archived and cleaned news, press releases and announcements from official
// city and county government websites. A sibling to localCandidatesApi and
// electionsApi, sharing their `pick` reader and their no-auth rule: api2 is
// read-only and public, so do NOT send the api.govglance.org bearer token here.
//
// Not to be confused with the search.govglance.org public-search behind
// useLocalNews. That answers "what is the web saying about this city"; this
// answers "what has this city's own government published". Both are useful
// and neither replaces the other, so they are kept apart by name.
//
// ─── Why this takes two requests ──────────────────────────────────────────────
//
// No endpoint takes a city name, and the API's own docs say why: a name does
// not identify a place. Eight states have a Springfield, and "Keokuk" is both a
// city and the county around it. So a name is resolved to a GEOID first, and
// the GEOID is what everything else is keyed on.
//
//   1. /jurisdictions?q=carmel&state=IN&kind=place  -> { geoid: "1810342", ... }
//   2. /jurisdictions/1810342/articles?limit=20
//
// `kind=place` is what separates a city from the county of the same name, and
// `state` is what stops a common name returning nine cities.
//
// A jurisdiction that is known but not yet crawled comes back with
// article_count 0 and a valid, empty feed rather than a 404. That distinction
// is carried through to the caller, because "we have not crawled this city yet"
// and "this city is not in the archive" are different things to tell somebody.
//
// Fields are read through `pick` because these endpoints declare no response
// schema. Names marked GUESS have not been confirmed against a live response.

import { API2_BASE, pick } from './localCandidatesApi';

export const DEFAULT_ARTICLE_LIMIT = 20;

// A city, a town, a village -- anything that is a settlement rather than the
// county around it.
export const PLACE_KIND = 'place';
export const COUNTY_KIND = 'county';

const cache = new Map();

export const clearJurisdictionNewsCache = () => cache.clear();

const readRows = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
};

const requestJson = async (path, params, { signal } = {}) => {
  const query = params ? `?${params}` : '';
  const response = await fetch(`${API2_BASE}${path}${query}`, { signal });

  if (!response.ok) {
    // api2 names the rejected parameter in the body; the status alone does not
    // say what to change.
    let detail = '';
    try {
      const body = await response.text();
      detail = body ? ` - ${body.slice(0, 300)}` : '';
    } catch {
      // The status is still worth reporting.
    }
    const error = new Error(`api2 ${path} responded ${response.status}${detail}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
};

// ─── Jurisdictions ────────────────────────────────────────────────────────────

const normalizeJurisdiction = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const geoid = pick(raw, 'geoid');
  const name = pick(raw, 'name', 'jurisdiction_name');
  if (!geoid || !name) return null;

  return {
    geoid: String(geoid),
    name: String(name).trim(),
    stateAbbr: String(pick(raw, 'state', 'state_abbr') || '').toUpperCase(),
    kind: pick(raw, 'kind') || null,

    // 0 for a jurisdiction that is known but has not been crawled. Carried
    // rather than dropped: an empty feed for a city we know about reads very
    // differently from a city we have never heard of.
    articleCount: Number(pick(raw, 'article_count') ?? 0) || 0,
    website: pick(raw, 'website', 'official_website', 'url'), // GUESS
  };
};

/**
 * Resolve a city name and state to the archive's own GEOID.
 *
 * Returns the jurisdiction, or null when the archive has no such place. An
 * exact name match wins over the first result: searching "Carmel" in Indiana
 * should not land on "Carmel Township" because it sorted first.
 */
export const resolveJurisdiction = async (
  { city, stateAbbr, kind = PLACE_KIND },
  { signal } = {}
) => {
  if (!city || !stateAbbr) return null;

  const params = new URLSearchParams({
    q: String(city).trim(),
    state: String(stateAbbr).toUpperCase(),
    kind,
    limit: '20',
  });

  const cacheKey = `jurisdiction?${params}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const payload = await requestJson('/jurisdictions', params, { signal });
  const rows = readRows(payload, 'jurisdictions', 'results')
    .map(normalizeJurisdiction)
    .filter(Boolean);

  const wanted = String(city).trim().toLowerCase();
  const exact = rows.find((row) => row.name.toLowerCase() === wanted);

  // Prefer a crawled match over an uncrawled one when neither is exact -- a
  // feed with articles in it is more useful than a placeholder row.
  const best = exact || rows.find((row) => row.articleCount > 0) || rows[0] || null;

  cache.set(cacheKey, best);
  return best;
};

// ─── Articles ─────────────────────────────────────────────────────────────────

export const formatArticleDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Turn a category slug ("press_release") into a label ("Press release").
export const formatCategory = (value) => {
  const text = String(value || '').replace(/_/g, ' ').trim();
  if (!text) return null;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const normalizeArticle = (raw, index = 0) => {
  if (!raw || typeof raw !== 'object') return null;

  const title = pick(raw, 'title', 'headline');
  if (!title) return null;

  const published = pick(raw, 'published_at', 'published', 'date', 'created_at');

  return {
    id: pick(raw, 'id'),
    title: String(title).trim(),

    // The link as the government published it. Every archived record keeps the
    // original alongside the resolved one; the original is what a reader should
    // be sent to, since it is the address the government itself advertises.
    url: pick(raw, 'url', 'link', 'resolved_url', 'source_url'),

    summary: pick(raw, 'summary', 'description'),
    category: pick(raw, 'category'),
    categoryLabel: formatCategory(pick(raw, 'category')),

    jurisdictionName: pick(raw, 'jurisdiction_name', 'name'), // GUESS
    stateAbbr: String(pick(raw, 'state', 'state_abbr') || '').toUpperCase(),

    publishedAt: published || null,
    publishedLabel: formatArticleDate(published),

    order: index,
    key: pick(raw, 'id') ? `article:${pick(raw, 'id')}` : `article:${index}:${title}`,
  };
};

/**
 * Recent articles for one jurisdiction, newest first.
 *
 * `include_body` is left off: the panel shows a title, a date and a link, and
 * the body is the largest field on the row.
 */
export const fetchJurisdictionArticles = async (
  geoid,
  { limit = DEFAULT_ARTICLE_LIMIT, offset = 0, signal } = {}
) => {
  if (!geoid) return [];

  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  const cacheKey = `articles/${geoid}?${params}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const payload = await requestJson(
    `/jurisdictions/${encodeURIComponent(geoid)}/articles`,
    params,
    { signal }
  );

  const articles = readRows(payload, 'articles', 'results')
    .map(normalizeArticle)
    .filter(Boolean);

  cache.set(cacheKey, articles);
  return articles;
};

/**
 * One call for a city: resolve it, then read its feed.
 *
 * Returns { jurisdiction, articles }. A jurisdiction of null means the archive
 * has no such place; a jurisdiction with an empty articles array means it is
 * known but not yet crawled. The caller needs both of those to say anything
 * true about an empty screen.
 */
export const fetchJurisdictionNews = async (
  { city, stateAbbr, kind = PLACE_KIND, limit = DEFAULT_ARTICLE_LIMIT },
  { signal } = {}
) => {
  const jurisdiction = await resolveJurisdiction({ city, stateAbbr, kind }, { signal });
  if (!jurisdiction) return { jurisdiction: null, articles: [] };

  const articles = await fetchJurisdictionArticles(jurisdiction.geoid, { limit, signal });
  return { jurisdiction, articles };
};